import ApexCharts, { ApexOptions } from 'apexcharts';
import { FC, useEffect } from 'react';
import { useRef } from 'react';
import { getCSSVariableValue } from '../../../assets/ts/_utils';
import { calculateLoan } from '../../../helpers';
import { IActiveLoanMetric } from '../../../interfaces/metrics';

type Props = {
  activeLoanMetric: IActiveLoanMetric;
};

const LoanApplicationMetric: FC<Props> = ({ activeLoanMetric }) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const { amount, offer_tenure, interest, title } = activeLoanMetric;

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    const chart = new ApexCharts(chartRef.current, chartOptions('warning', '140px', activeLoanMetric));
    if (chart) {
      chart.render();
    }

    return () => {
      if (chart) {
        chart.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRef]);
  return (
    <div>
      <h6 className='fs-7 fw-bolder'>{title}</h6>
      <div className='d-flex justify-content-between align-items-center'>
        <div className=''>
          <label className='text-muted fs-8 fw-bolder'>Total Loan Amount</label>
          <h1 className='fw-700'>{calculateLoan(+amount, +interest, offer_tenure)?.totalAmountPaid?.toLocaleString()}</h1>
        </div>
        <div className='flex-grow-1 text-end'>
          <div ref={chartRef} className='mixed-widget-4-chart text-end align-end'></div>
        </div>
      </div>
      <div className='d-flex justify-content-between mt-5 mb-10'>
        <div className='d-flex align-items-center me-2'>
          <div className='symbol symbol-40px me-3'>
            <div className='symbol-label bg-light-warning'>
              <i className='bi bi-bookmark-check-fill text-warning'></i>
            </div>
          </div>
          <div>
            <div className='fs-6 text-dark fw-bolder'>{activeLoanMetric.paid_repayments}</div>
            <div className='fs-8 text-muted fw-bold' style={{ lineHeight: '0.8rem' }}>
              Repayments Made
            </div>
          </div>
        </div>

        <div className='d-flex align-items-center me-2'>
          <div className='symbol symbol-40px me-3'>
            <div className='symbol-label bg-light-success'>
              <i className='bi bi-bookmark-dash-fill text-success'></i>
            </div>
          </div>
          <div>
            <div className='fs-6 text-dark fw-bolder'>{activeLoanMetric.active_repayments}</div>
            <div className='fs-8 text-muted fw-bold' style={{ lineHeight: '0.8rem' }}>
              Active Repayments
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const chartOptions = (chartColor: string, chartHeight: string, activeLoanMetric: IActiveLoanMetric): ApexOptions => {
  const baseColor = getCSSVariableValue('--bs-' + chartColor);
  const lightColor = getCSSVariableValue('--bs-light-' + chartColor);
  const labelColor = getCSSVariableValue('--bs-gray-700');
  const { paid_repayments, active_repayments } = activeLoanMetric;

  return {
    series: [Math.round((+paid_repayments / (+paid_repayments + +active_repayments)) * 100)],
    chart: {
      fontFamily: 'inherit',
      height: chartHeight,
      type: 'radialBar',
    },
    plotOptions: {
      radialBar: {
        hollow: {
          margin: 0,
          size: '65%',
        },
        dataLabels: {
          name: {
            show: false,
            fontWeight: '700',
          },
          value: {
            color: labelColor,
            fontSize: '20px',
            fontWeight: '700',
            offsetY: 12,
            show: true,
            formatter: function (val) {
              return val + '%';
            },
          },
        },
        track: {
          background: lightColor,
          strokeWidth: '100%',
        },
      },
    },
    colors: [baseColor],
    stroke: {
      lineCap: 'round',
    },
    labels: ['Progress'],
  };
};

export default LoanApplicationMetric;
